import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { ScoreBar } from "../../components/Charts";
import { PublicLayout } from "../../components/PanelLayout";
import { Alert, Badge, Card, Loading } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { dateBR, score } from "../../lib/format";

interface PublicReport {
  title: string;
  date: string;
  durationSeconds: number;
  scores: {
    overall: number;
    fluency: number;
    vocabulary: number;
    grammar: number;
    comprehension: number;
  };
  objectives: { objective: string; achieved: boolean }[];
  strengths: string[];
  practice: string[];
  summaryPt: string;
  corrections: { said: string; better: string; why: string }[];
  vocabularyUsed: string[];
}

export function ReportPage() {
  const { token = "" } = useParams();
  const q = useQuery({
    queryKey: ["report", token],
    queryFn: () => api.get<PublicReport>(`/v1/reports/${token}`),
    retry: false,
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) {
    return (
      <PublicLayout>
        <Alert kind="risk">{errorMessage(q.error)}</Alert>
      </PublicLayout>
    );
  }
  const r = q.data;
  return (
    <PublicLayout>
      <Card>
        <p className="small muted">
          {dateBR(r.date)} · {Math.round(r.durationSeconds / 60)} min de conversa
        </p>
        <h1 style={{ marginTop: 4 }}>{r.title}</h1>
        <div className="brand-stripe" aria-hidden>
          <span />
          <span />
          <span />
          <span />
        </div>
        <div className="stat" style={{ marginTop: 16 }}>
          <div className="label">Nota geral</div>
          <div className="value">{score(r.scores.overall)}</div>
        </div>
        <p style={{ marginTop: 12 }}>{r.summaryPt}</p>
      </Card>
      <Card title="Suas notas">
        <div className="stack">
          <ScoreBar label="Fluência" value={r.scores.fluency} />
          <ScoreBar label="Vocabulário" value={r.scores.vocabulary} />
          <ScoreBar label="Gramática" value={r.scores.grammar} />
          <ScoreBar label="Compreensão" value={r.scores.comprehension} />
        </div>
      </Card>
      <Card title="Objetivos">
        <div className="stack">
          {r.objectives.map((o) => (
            <div key={o.objective} className="row between">
              <span>{o.objective}</span>
              <Badge color={o.achieved ? "green" : "gray"}>
                {o.achieved ? "Cumprido" : "Na próxima"}
              </Badge>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Pontos fortes">
        <ul>
          {r.strengths.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </Card>
      <Card title="Para praticar">
        <ul>
          {r.practice.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        {r.corrections.length ? (
          <div className="stack" style={{ marginTop: 12 }}>
            {r.corrections.map((c, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: lista fixa do relatório, sem reordenação.
              <div key={`${c.said}-${i}`} className="small">
                Você disse: <em>{c.said}</em>
                <br />
                Melhor: <strong>{c.better}</strong>
                <div className="muted">{c.why}</div>
              </div>
            ))}
          </div>
        ) : null}
      </Card>
    </PublicLayout>
  );
}
